import api from './api';

export interface HistoricoIndicacao {
  id: number;
  cliente_indicador_id: number;
  cliente_indicado_id: number;
  nivel: number;
  valor_bonificacao: number;
  tipo_bonificacao: 'cadastro' | 'recorrente';
  data_processamento: string;
  fatura_id?: number;
  credito_id?: number;
  cliente_indicado?: {
    id: number;
    nome: string;
    status: string;
  };
}

export interface EstatisticasIndicacoes {
  creditosDisponiveis: number;
  creditosUtilizados: number;
  totalCreditos: number;
  indicacoesNivel1: number;
  indicacoesNivel2: number;
  indicacoesNivel3: number;
  totalIndicacoes: number;
}

export interface ClienteIndicado {
  id: number;
  nome: string;
  cpf_cnpj: string;
  status: string;
  createdAt: string;
  totalIndicados: number;
  totalCreditosGerados: number;
}

export interface ArvoreIndicacao {
  id: number;
  nome: string;
  status: string;
  createdAt: string;
  indicados: ArvoreIndicacao[];
}

export interface ResumoBonificacoes {
  periodo: {
    dataInicio: string;
    dataFim: string;
  };
  totais: {
    geral: number;
    cadastro: number;
    recorrente: number;
  };
  porNivel: Array<{
    nivel: number;
    total: number;
    quantidade: number;
  }>;
}

export interface RankingIndicador {
  posicao: number;
  cliente: {
    id: number;
    nome: string;
  };
  totalBonificacoes: number;
  totalIndicacoes: number;
}

export interface RankingResponse {
  periodo: {
    tipo: string;
    dataInicio: string;
    dataFim: string;
  };
  ranking: RankingIndicador[];
}

export interface PaginatedResponse<T> {
  historico?: T[];
  clientes?: ClienteIndicado[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface HistoricoFilters {
  page?: number;
  limit?: number;
  tipo?: 'cadastro' | 'recorrente';
  nivel?: number;
}

export interface ClientesFilters {
  page?: number;
  limit?: number;
  status?: 'ativo' | 'inativo' | 'suspenso' | 'inadimplente';
}

class IndicacaoService {

  /**
   * Busca histórico de indicações de um cliente
   */
  async getHistoricoIndicacoes(
    clienteId: number, 
    filters: HistoricoFilters = {}
  ): Promise<PaginatedResponse<HistoricoIndicacao>> {
    const params = new URLSearchParams();
    
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.tipo) params.append('tipo', filters.tipo);
    if (filters.nivel) params.append('nivel', filters.nivel.toString());

    const response = await api.get(`/indicacoes/${clienteId}/historico?${params.toString()}`);
    return response as unknown as PaginatedResponse<HistoricoIndicacao>;
  }

  /**
   * Busca árvore de indicações de um cliente
   */
  async getArvoreIndicacoes(clienteId: number): Promise<ArvoreIndicacao[]> {
    const response = await api.get(`/indicacoes/${clienteId}/arvore`);
    return response as unknown as ArvoreIndicacao[];
  }

  /**
   * Busca estatísticas de indicações de um cliente
   */
  async getEstatisticasIndicacoes(clienteId: number): Promise<EstatisticasIndicacoes> {
    const response = await api.get(`/indicacoes/${clienteId}/estatisticas`);
    return response as unknown as EstatisticasIndicacoes;
  }

  /**
   * Busca clientes indicados diretamente (nível 1)
   */
  async getClientesIndicadosNivel1(
    clienteId: number, 
    filters: ClientesFilters = {}
  ): Promise<PaginatedResponse<ClienteIndicado>> {
    const params = new URLSearchParams();
    
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.status) params.append('status', filters.status);

    const response = await api.get(`/indicacoes/${clienteId}/nivel1?${params.toString()}`);
    return response as unknown as PaginatedResponse<ClienteIndicado>;
  }

  /**
   * Busca resumo de bonificações por período
   */
  async getResumoBonificacoesPeriodo(
    clienteId: number,
    dataInicio: string,
    dataFim: string
  ): Promise<ResumoBonificacoes> {
    const params = new URLSearchParams();
    params.append('dataInicio', dataInicio);
    params.append('dataFim', dataFim);

    const response = await api.get(`/indicacoes/${clienteId}/bonificacoes-periodo?${params.toString()}`);
    return response as unknown as ResumoBonificacoes;
  }

  /**
   * Busca ranking de indicadores
   */
  async getRankingIndicadores(
    limit: number = 10,
    periodo: 'semana' | 'mes' | 'trimestre' | 'ano' = 'mes'
  ): Promise<RankingResponse> {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    params.append('periodo', periodo);

    const response = await api.get(`/indicacoes/ranking?${params.toString()}`);
    return response as unknown as RankingResponse;
  }

  /**
   * Formata tipo de bonificação para exibição
   */
  formatTipoBonificacao(tipo: 'cadastro' | 'recorrente'): string {
    return tipo === 'cadastro' ? 'Cadastro' : 'Recorrente';
  }

  /**
   * Retorna cor do chip baseado no tipo de bonificação
   */
  getTipoBonificacaoColor(tipo: 'cadastro' | 'recorrente'): 'success' | 'info' {
    return tipo === 'cadastro' ? 'success' : 'info';
  }

  /**
   * Retorna ícone baseado no tipo de bonificação
   */
  getTipoBonificacaoIcon(tipo: 'cadastro' | 'recorrente'): string {
    return tipo === 'cadastro' ? 'person_add' : 'repeat';
  }

  /**
   * Retorna cor baseada no status do cliente
   */
  getStatusColor(status: string): 'success' | 'warning' | 'error' | 'default' {
    switch (status) {
      case 'ativo': return 'success';
      case 'suspenso': return 'warning';
      case 'inadimplente': return 'error';
      default: return 'default';
    }
  }

  /**
   * Formata status do cliente
   */
  formatStatus(status: string): string {
    const statusMap: Record<string, string> = {
      'ativo': 'Ativo',
      'inativo': 'Inativo',
      'suspenso': 'Suspenso',
      'inadimplente': 'Inadimplente'
    };
    return statusMap[status] || status;
  }

  /**
   * Formata valor monetário
   */
  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  /**
   * Formata data para exibição
   */
  formatDate(date: string): string {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(new Date(date));
  }

  /**
   * Formata data e hora para exibição
   */
  formatDateTime(date: string): string {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(date));
  }

  /**
   * Calcula total de clientes na árvore
   */
  calcularTotalArvore(arvore: ArvoreIndicacao[]): number {
    return arvore.reduce((total, cliente) => {
      return total + 1 + this.calcularTotalArvore(cliente.indicados);
    }, 0);
  }

  /**
   * Calcula profundidade máxima da árvore
   */
  calcularProfundidadeMaxima(arvore: ArvoreIndicacao[]): number {
    if (arvore.length === 0) return 0;
    
    return Math.max(...arvore.map(cliente => 
      1 + this.calcularProfundidadeMaxima(cliente.indicados)
    ));
  }

  /**
   * Busca cliente na árvore por ID
   */
  encontrarClienteNaArvore(arvore: ArvoreIndicacao[], clienteId: number): ArvoreIndicacao | null {
    for (const cliente of arvore) {
      if (cliente.id === clienteId) {
        return cliente;
      }
      
      const encontrado = this.encontrarClienteNaArvore(cliente.indicados, clienteId);
      if (encontrado) {
        return encontrado;
      }
    }
    
    return null;
  }

  /**
   * Agrupa histórico por mês
   */
  agruparHistoricoPorMes(historico: HistoricoIndicacao[]): Record<string, HistoricoIndicacao[]> {
    return historico.reduce((acc, item) => {
      const data = new Date(item.data_processamento);
      const chave = `${data.getFullYear()}-${(data.getMonth() + 1).toString().padStart(2, '0')}`;
      
      if (!acc[chave]) {
        acc[chave] = [];
      }
      
      acc[chave].push(item);
      return acc;
    }, {} as Record<string, HistoricoIndicacao[]>);
  }

  /**
   * Calcula crescimento entre períodos
   */
  calcularCrescimento(valorAtual: number, valorAnterior: number): {
    percentual: number;
    absoluto: number;
    cresceu: boolean;
  } {
    const absoluto = valorAtual - valorAnterior;
    const percentual = valorAnterior > 0 ? (absoluto / valorAnterior) * 100 : 0;
    
    return {
      percentual: Math.round(percentual * 100) / 100,
      absoluto,
      cresceu: absoluto > 0
    };
  }

  /**
   * Gera dados para gráfico de evolução
   */
  gerarDadosGraficoEvolucao(historico: HistoricoIndicacao[]): Array<{
    mes: string;
    cadastros: number;
    recorrentes: number;
    total: number;
  }> {
    const agrupado = this.agruparHistoricoPorMes(historico);
    
    return Object.entries(agrupado)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([mes, items]) => {
        const cadastros = items
          .filter(item => item.tipo_bonificacao === 'cadastro')
          .reduce((sum, item) => sum + item.valor_bonificacao, 0);
        
        const recorrentes = items
          .filter(item => item.tipo_bonificacao === 'recorrente')
          .reduce((sum, item) => sum + item.valor_bonificacao, 0);
        
        return {
          mes,
          cadastros,
          recorrentes,
          total: cadastros + recorrentes
        };
      });
  }

  /**
   * Valida período para consulta
   */
  validatePeriodo(dataInicio: string, dataFim: string): string[] {
    const errors: string[] = [];
    
    const inicio = new Date(dataInicio);
    const fim = new Date(dataFim);
    const agora = new Date();
    
    if (inicio > fim) {
      errors.push('Data de início deve ser anterior à data de fim');
    }
    
    if (fim > agora) {
      errors.push('Data de fim não pode ser superior à data atual');
    }
    
    const diffMs = fim.getTime() - inicio.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays > 365) {
      errors.push('Período não pode ser superior a 365 dias');
    }
    
    return errors;
  }
}

export default new IndicacaoService(); 